import { useState } from 'react';
import { tripService } from '../../../services/tripService';
import { SavedTrip } from '../../../types/trip';
import { TripSummary } from '../../../types/tripSummary';
import { QuoteFormData } from '../../../types/quote';
import { Timestamp } from 'firebase/firestore';

interface QuickLocation {
  name: string;
  coordinates: [number, number];
}

interface RouteSegment {
  fromIndex: number;
  toIndex: number;
  distance: number;
  duration: number;
  isApproximate: boolean;
  geometry: [number, number][];
  trainRoute?: any;
}

type TransportMode = 'car' | 'bus' | 'train';

interface UseSavedTripsParams {
  userId: string | undefined;
  apiBaseUrl: string;
  convertRouteSegmentForFirestore: (segment: RouteSegment) => any;
  convertRouteSegmentFromFirestore: (segment: any) => RouteSegment;
}

interface SaveTripParams {
  currentView: string;
  currentTripId: string | null;
  dayTripName: string;
  multiDayTripName: string;
  // Day trip data
  startLocation: QuickLocation | null;
  tripPlaces: any[];
  dayTransportation: Map<number, Map<number, TransportMode>>;
  dayRouteSegments: Map<number, RouteSegment[]>;
  selectedTypes: any[];
  maxDistance: number;
  aiReview: string | null;
  isTripFinalized: boolean;
  // Multi-day trip data
  multiDayStartDate: Date | null;
  multiDayEndDate: Date | null;
  multiDayAdults: number;
  multiDayChildren: number;
  multiDayInfants: number;
  multiDayStartLocation: QuickLocation | null;
  dayLocations: Map<number, QuickLocation>;
  finalizedDays: Map<number, any>;
  combinedDayBlocks: Map<number, number[]>;
  isTripEnded: boolean;
  tripEndLocation: QuickLocation | null;
  tripEndRouteSegment: RouteSegment | null;
  tripEndTransportMode: TransportMode;
  tripEndTrainAvailable: boolean;
}

interface LoadTripCallbacks {
  setCurrentTripId: (id: string) => void;
  setDayTripName: (name: string) => void;
  setMultiDayTripName: (name: string) => void;
  setStartLocation: (loc: QuickLocation) => void;
  setTripPlaces: (places: any[]) => void;
  setSelectedTypes: (types: string[]) => void;
  setMaxDistance: (dist: number) => void;
  setAiReview: (review: string | null) => void;
  setIsTripFinalized: (finalized: boolean) => void;
  setDayTransportation: (transport: Map<number, Map<number, TransportMode>>) => void;
  setDayRouteSegments: (segments: Map<number, RouteSegment[]>) => void;
  setCurrentView: (view: string) => void;
  setMultiDayStartDate: (date: Date) => void;
  setMultiDayEndDate: (date: Date) => void;
  setMultiDayAdults: (count: number) => void;
  setMultiDayChildren: (count: number) => void;
  setMultiDayInfants: (count: number) => void;
  setMultiDayStartLocation: (loc: QuickLocation) => void;
  setDayLocations: (locs: Map<number, QuickLocation>) => void;
  setFinalizedDays: (days: Map<number, any>) => void;
  setCombinedDayBlocks: (blocks: Map<number, number[]>) => void;
  setTripEndLocation: (loc: QuickLocation | null) => void;
  setTripEndTransportMode: (mode: TransportMode) => void;
  setTripEndRouteSegment: (segment: RouteSegment | null) => void;
  setTripEndTrainAvailable: (available: boolean) => void;
  setIsTripEnded: (ended: boolean) => void;
  addTripEndMarker: (coords: [number, number]) => void;
  setHasUnsavedChanges: (hasChanges: boolean) => void;
  // Trip summary callbacks
  setTripSummary: (summary: any) => void;
  setPlacePhotos: (photos: Record<string, string>) => void;
  setShowSummaryModal: (show: boolean) => void;
}

/**
 * Custom hook to manage saved trips (save, load, delete, email)
 * 
 * Handles all trip persistence operations including save/load/delete,
 * data transformation for Firestore, and email itinerary functionality.
 * 
 * @param params - Configuration and transformation functions
 * @returns Trip management functions and state
 */
export function useSavedTrips(params: UseSavedTripsParams) {
  const { userId, apiBaseUrl, convertRouteSegmentForFirestore, convertRouteSegmentFromFirestore } = params;

  const [savedTrips, setSavedTrips] = useState<SavedTrip[]>([]);
  const [currentTripId, setCurrentTripId] = useState<string | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [isGeneratingSummary, setIsGeneratingSummary] = useState(false);
  const [tripSummary, setTripSummary] = useState<TripSummary | null>(null);
  const [placePhotos, setPlacePhotos] = useState<Record<string, string>>({});
  const [summaryError, setSummaryError] = useState<string | null>(null);

  const handleSaveTrip = async (saveParams: SaveTripParams) => {
    if (!userId) {
      return;
    }

    const {
      currentView,
      currentTripId,
      dayTripName,
      multiDayTripName,
      startLocation,
      tripPlaces,
      dayTransportation,
      dayRouteSegments,
      selectedTypes,
      maxDistance,
      aiReview,
      isTripFinalized,
      multiDayStartDate,
      multiDayEndDate,
      multiDayAdults,
      multiDayChildren,
      multiDayInfants,
      multiDayStartLocation,
      dayLocations,
      finalizedDays,
      combinedDayBlocks, // eslint-disable-line @typescript-eslint/no-unused-vars
      isTripEnded,
      tripEndLocation,
      tripEndRouteSegment,
      tripEndTransportMode,
      tripEndTrainAvailable,
    } = saveParams;

    try {
      const tripType = currentView.includes('multi-day') ? 'multi-day-trip' : 'day-trip';
      const tripName = tripType === 'day-trip' ? dayTripName : multiDayTripName;

      // Validate trip name
      if (!tripName || tripName.trim() === '') {
        alert('Please provide a name for your trip');
        return;
      }

      const tripData: SavedTrip = {
        id: currentTripId || '',
        userId: userId,
        tripName: tripName,
        tripType: tripType,
        createdAt: null as any,
        updatedAt: null as any,
      };

      if (tripData.tripType === 'day-trip') {
        // Simplify places data
        const simplifiedPlaces = tripPlaces.map(place => {
          let geoPoint: any;

          if (place.geo_point) {
            if (Array.isArray(place.geo_point)) {
              geoPoint = { lon: place.geo_point[0], lat: place.geo_point[1] };
            } else if (typeof place.geo_point === 'object') {
              geoPoint = {
                lat: place.geo_point.lat || place.geo_point.latitude,
                lon: place.geo_point.lon || place.geo_point.lng || place.geo_point.longitude
              };
            }
          } else if (place.coordinates) {
            if (Array.isArray(place.coordinates)) {
              geoPoint = { lon: place.coordinates[0], lat: place.coordinates[1] };
            } else if (typeof place.coordinates === 'object') {
              geoPoint = {
                lat: place.coordinates.lat || place.coordinates.latitude,
                lon: place.coordinates.lon || place.coordinates.lng || place.coordinates.longitude
              };
            }
          }

          const simplified: any = {
            id: place.id,
            title: place.title || place.name,
            type: place.type,
            geo_point: geoPoint
          };

          // Only add fields if they have values
          if (place.photo_1_cdn || place.imageUrl) simplified.photo_1_cdn = place.photo_1_cdn || place.imageUrl;
          if (place.photo_2_cdn) simplified.photo_2_cdn = place.photo_2_cdn;
          if (place.photo_3_cdn) simplified.photo_3_cdn = place.photo_3_cdn;
          if (place.photo_4_cdn) simplified.photo_4_cdn = place.photo_4_cdn;
          if (place.photo_5_cdn) simplified.photo_5_cdn = place.photo_5_cdn;
          if (place.ideal_duration) simplified.ideal_duration = place.ideal_duration;
          if (place.typical_time_spent) simplified.typical_time_spent = place.typical_time_spent;
          if (place.city) simplified.city = place.city;
          if (place.rating) simplified.rating = place.rating;

          return simplified;
        });

        tripData.dayTrip = {
          startLocation: startLocation!,
          places: simplifiedPlaces,
          transportation: Object.fromEntries(
            Array.from(dayTransportation.get(0) || new Map())
          ),
          routeSegments: (dayRouteSegments.get(0) || []).map(convertRouteSegmentForFirestore),
          filters: { selectedTypes, maxDistance },
          aiReview: aiReview || undefined,
          isFinalized: isTripFinalized
        };
      } else {
        const finalizedDaysRecord: Record<number, any> = {};
        finalizedDays.forEach((data, dayIndex) => {
          // Simplify places
          const simplifiedPlaces = data.places.map((place: any) => {
            let geoPoint: any;

            if (place.geo_point) {
              if (Array.isArray(place.geo_point)) {
                geoPoint = { lon: place.geo_point[0], lat: place.geo_point[1] };
              } else if (typeof place.geo_point === 'object') {
                geoPoint = {
                  lat: place.geo_point.lat || place.geo_point.latitude,
                  lon: place.geo_point.lon || place.geo_point.lng || place.geo_point.longitude
                };
              }
            } else if (place.coordinates) {
              if (Array.isArray(place.coordinates)) {
                geoPoint = { lon: place.coordinates[0], lat: place.coordinates[1] };
              } else if (typeof place.coordinates === 'object') {
                geoPoint = {
                  lat: place.coordinates.lat || place.coordinates.latitude,
                  lon: place.coordinates.lon || place.coordinates.lng || place.coordinates.longitude
                };
              }
            }

            const simplified: any = {
              id: place.id,
              title: place.title || place.name,
              type: place.type,
              geo_point: geoPoint
            };

            if (place.photo_1_cdn || place.imageUrl) simplified.photo_1_cdn = place.photo_1_cdn || place.imageUrl;
            if (place.photo_2_cdn) simplified.photo_2_cdn = place.photo_2_cdn;
            if (place.photo_3_cdn) simplified.photo_3_cdn = place.photo_3_cdn;
            if (place.photo_4_cdn) simplified.photo_4_cdn = place.photo_4_cdn;
            if (place.photo_5_cdn) simplified.photo_5_cdn = place.photo_5_cdn;
            if (place.ideal_duration) simplified.ideal_duration = place.ideal_duration;
            if (place.typical_time_spent) simplified.typical_time_spent = place.typical_time_spent;
            if (place.city) simplified.city = place.city;
            if (place.rating) simplified.rating = place.rating;

            return simplified;
          });

          finalizedDaysRecord[dayIndex] = {
            places: simplifiedPlaces,
            transportation: Object.fromEntries(data.transportation),
            routeSegments: data.routeSegments.map(convertRouteSegmentForFirestore),
            startLocation: data.startLocation,
            ...(data.isMultiDayBlock && {
              isMultiDayBlock: true,
              blockDayIndices: data.blockDayIndices,
              blockStartDate: data.blockStartDate?.toISOString(),
              blockEndDate: data.blockEndDate?.toISOString()
            })
          };
        });

        const dayLocationsRecord: Record<number, QuickLocation> = {};
        dayLocations.forEach((loc, idx) => {
          dayLocationsRecord[idx] = loc;
        });

        const multiDayTripData: any = {
          startDate: Timestamp.fromDate(multiDayStartDate!),
          endDate: Timestamp.fromDate(multiDayEndDate!),
          travelers: {
            adults: multiDayAdults,
            children: multiDayChildren,
            infants: multiDayInfants
          },
          startLocation: multiDayStartLocation!,
          dayLocations: dayLocationsRecord,
          finalizedDays: finalizedDaysRecord
        };

        // Add trip end if exists
        if (isTripEnded && tripEndRouteSegment && tripEndLocation) {
          multiDayTripData.tripEnd = {
            location: tripEndLocation,
            transportMode: tripEndTransportMode,
            routeSegment: convertRouteSegmentForFirestore(tripEndRouteSegment),
            trainAvailable: tripEndTrainAvailable
          };
        }

        tripData.multiDayTrip = multiDayTripData;
      }

      const tripId = await tripService.saveTrip(userId, tripData);
      setCurrentTripId(tripId);
      setHasUnsavedChanges(false);

      alert('Trip saved successfully!');
    } catch (error) {
      alert('Error saving trip. Check console for details.');
    }
  };

  const handleLoadTrip = async (tripId: string, callbacks: LoadTripCallbacks) => {
    if (!userId) return;

    try {
      const trip = await tripService.loadTrip(userId, tripId);
      if (!trip) return;

      callbacks.setCurrentTripId(trip.id);

      if (trip.tripType === 'day-trip' && trip.dayTrip) {
        const dt = trip.dayTrip;
        callbacks.setDayTripName(trip.tripName);
        callbacks.setStartLocation(dt.startLocation);
        callbacks.setTripPlaces(dt.places);
        callbacks.setSelectedTypes(dt.filters.selectedTypes);
        callbacks.setMaxDistance(dt.filters.maxDistance);
        callbacks.setAiReview(dt.aiReview || null);
        callbacks.setIsTripFinalized(dt.isFinalized);

        // Restore transportation
        const transportMap = new Map<number, TransportMode>();
        Object.entries(dt.transportation).forEach(([idx, mode]) => {
          transportMap.set(Number(idx), mode as TransportMode);
        });
        callbacks.setDayTransportation(new Map([[0, transportMap]]));

        // Convert route segments
        const convertedSegments = dt.routeSegments.map(convertRouteSegmentFromFirestore);
        callbacks.setDayRouteSegments(new Map([[0, convertedSegments]]));

        callbacks.setCurrentView('my-day-trip');
      } else if (trip.tripType === 'multi-day-trip' && trip.multiDayTrip) {
        const mt = trip.multiDayTrip;
        callbacks.setMultiDayTripName(trip.tripName);
        callbacks.setMultiDayStartDate(mt.startDate.toDate());
        callbacks.setMultiDayEndDate(mt.endDate.toDate());
        callbacks.setMultiDayAdults(mt.travelers.adults);
        callbacks.setMultiDayChildren(mt.travelers.children);
        callbacks.setMultiDayInfants(mt.travelers.infants);
        callbacks.setMultiDayStartLocation(mt.startLocation);

        // Restore day locations
        const locMap = new Map<number, QuickLocation>();
        Object.entries(mt.dayLocations).forEach(([idx, loc]) => {
          locMap.set(Number(idx), loc);
        });
        callbacks.setDayLocations(locMap);

        // Restore finalized days
        const finalizedMap = new Map<number, any>();
        const combinedBlocksMap = new Map<number, number[]>();

        Object.entries(mt.finalizedDays).forEach(([idx, data]: [string, any]) => {
          const dayIndex = Number(idx);

          const transportMap = new Map<number, TransportMode>();
          Object.entries(data.transportation).forEach(([segIdx, mode]) => {
            transportMap.set(Number(segIdx), mode as TransportMode);
          });

          const convertedSegments = data.routeSegments.map(convertRouteSegmentFromFirestore);

          finalizedMap.set(dayIndex, {
            ...data,
            transportation: transportMap,
            routeSegments: convertedSegments,
            ...(data.isMultiDayBlock && {
              isMultiDayBlock: true,
              blockDayIndices: data.blockDayIndices,
              blockStartDate: new Date(data.blockStartDate),
              blockEndDate: new Date(data.blockEndDate)
            })
          });

          // Build combinedDayBlocks map
          if (data.isMultiDayBlock && data.blockDayIndices) {
            combinedBlocksMap.set(dayIndex, data.blockDayIndices);
          }
        });

        callbacks.setFinalizedDays(finalizedMap);
        callbacks.setCombinedDayBlocks(combinedBlocksMap);

        // Restore trip end
        if (mt.tripEnd) {
          callbacks.setTripEndLocation(mt.tripEnd.location);
          callbacks.setTripEndTransportMode(mt.tripEnd.transportMode);
          callbacks.setTripEndRouteSegment(convertRouteSegmentFromFirestore(mt.tripEnd.routeSegment));
          callbacks.setTripEndTrainAvailable(mt.tripEnd.trainAvailable);
          callbacks.setIsTripEnded(true);
          callbacks.addTripEndMarker(mt.tripEnd.location.coordinates);
        }

        // Decide which view to open based on trip summary status
        // If trip summary exists and is current (not stale), open summary view
        // Otherwise, open itinerary view
        let shouldOpenSummary = false;
        
        if (trip.tripSummary && trip.tripSummary.generatedAt && trip.updatedAt) {
          // Convert timestamps to Date objects for comparison
          const generatedAtDate = (trip.tripSummary.generatedAt as any).toDate ? 
            (trip.tripSummary.generatedAt as any).toDate() : 
            (trip.tripSummary.generatedAt instanceof Date ? trip.tripSummary.generatedAt : new Date());
          
          const updatedAtDate = (trip.updatedAt as any).toDate ? 
            (trip.updatedAt as any).toDate() : 
            (trip.updatedAt instanceof Date ? trip.updatedAt : new Date());
          
          // Summary is current if generatedAt is equal to or newer than updatedAt
          if (generatedAtDate >= updatedAtDate) {
            shouldOpenSummary = true;
            
            // Load the trip summary content into state
            callbacks.setTripSummary(trip.tripSummary.content);
            
            // Load place photos if they exist (they're usually cached in the summary)
            // If not present, we'll set empty object
            callbacks.setPlacePhotos({});
            
            console.log('[useSavedTrips] Opening trip summary view - summary is current', {
              generatedAt: generatedAtDate,
              updatedAt: updatedAtDate
            });
          } else {
            console.log('[useSavedTrips] Opening itinerary view - summary is stale', {
              generatedAt: generatedAtDate,
              updatedAt: updatedAtDate
            });
          }
        } else {
          console.log('[useSavedTrips] Opening itinerary view - no summary exists');
        }

        if (shouldOpenSummary) {
          // Open summary modal instead of itinerary view
          callbacks.setCurrentView('multi-day-trip-itinerary'); // Still need to set the base view
          callbacks.setShowSummaryModal(true); // Then open the summary modal
        } else {
          // Open itinerary view as usual
          callbacks.setCurrentView('multi-day-trip-itinerary');
        }
      }

      callbacks.setHasUnsavedChanges(false);
    } catch (error) {
      // Error handled silently
    }
  };

  const handleDeleteTrip = async (tripId: string) => {
    if (!userId) return;

    try {
      await tripService.deleteTrip(userId, tripId);
      setSavedTrips(prev => prev.filter(t => t.id !== tripId));
      alert('Trip deleted successfully');
    } catch (error) {
      alert('Error deleting trip');
    }
  };

  const handleEmailItinerary = async (
    tripId: string | null,
    userEmail: string | undefined,
    additionalEmail: string,
    emailAdditionalRecipients: boolean
  ) => {
    if (!userId || !tripId) {
      alert('Please save your trip first before emailing the itinerary.');
      return;
    }

    // Validate additional email if provided
    if (emailAdditionalRecipients && additionalEmail) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(additionalEmail)) {
        alert('Please enter a valid email address.');
        return;
      }
    }

    setIsSendingEmail(true);

    try {
      const additionalEmails = emailAdditionalRecipients && additionalEmail ? [additionalEmail] : [];

      const response = await fetch(`${apiBaseUrl}/api/advanced-trip/email-itinerary`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: userId,
          tripId: tripId,
          additionalEmails
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to send email');
      }

      alert(`Itinerary emailed successfully to ${userEmail}${additionalEmail ? ` and ${additionalEmail}` : ''}!`);

      return true;
    } catch (error) {
      alert('Error sending itinerary email. Please try again.');
      return false;
    } finally {
      setIsSendingEmail(false);
    }
  };

  /**
   * Generate AI-powered trip summary
   * For long trips (>5 days), returns 'use_async' signal to trigger async generation in Plan.tsx
   * For short trips (≤5 days), generates synchronously as before
   */
  const handleGenerateSummary = async (
    tripId: string, 
    optimizeOrder?: boolean,
    tripDaysCount?: number
  ): Promise<{ useAsync: boolean } | void> => {
    if (!userId) {
      return;
    }

    // If trip has more than 5 days, signal to use async generation
    // This prevents timeout issues with large trips
    if (tripDaysCount && tripDaysCount > 5) {
      console.log('[useSavedTrips] Trip has', tripDaysCount, 'days - using async generation');
      return { useAsync: true };
    }

    // For shorter trips (≤5 days), use synchronous generation as before
    console.log('[useSavedTrips] Trip has', tripDaysCount || 'unknown', 'days - using sync generation');
    setIsGeneratingSummary(true);
    setSummaryError(null);

    try {
      const result = await tripService.generateTripSummary(userId, tripId, false, optimizeOrder);
      
      setTripSummary(result.summary || null);
      setPlacePhotos(result.placePhotos || {});
      
      // Refresh the trip in savedTrips to get the new generatedAt timestamp
      const updatedTrip = await tripService.loadTrip(userId, tripId);
      if (updatedTrip) {
        setSavedTrips(prev => prev.map(t => t.id === tripId ? updatedTrip : t));
      }
    } catch (error: any) {
      setSummaryError(error.message || 'Failed to generate trip summary');
    } finally {
      setIsGeneratingSummary(false);
    }
  };

  /**
   * Clear trip summary state
   */
  const clearSummary = () => {
    setTripSummary(null);
    setPlacePhotos({});
    setSummaryError(null);
  };

  /**
   * Request a quote for the current trip or lazy quote
   */
  const handleRequestQuote = async (formData: QuoteFormData) => {
    // For lazy quotes, tripId is not required - backend will create one
    const isLazyQuote = (formData as any).isLazy === true;
    
    if (!userId) {
      throw new Error('User not authenticated');
    }
    
    if (!isLazyQuote && !currentTripId) {
      throw new Error('No trip selected');
    }

    try {
      const result = await tripService.requestQuote(
        userId,
        currentTripId || '',
        formData
      );
      
      return result;
    } catch (error: any) {
      throw error;
    }
  };

  return {
    savedTrips,
    setSavedTrips,
    currentTripId,
    setCurrentTripId,
    hasUnsavedChanges,
    setHasUnsavedChanges,
    isSendingEmail,
    isGeneratingSummary,
    tripSummary,
    setTripSummary,
    placePhotos,
    setPlacePhotos,
    summaryError,
    handleSaveTrip,
    handleLoadTrip,
    handleDeleteTrip,
    handleEmailItinerary,
    handleGenerateSummary,
    clearSummary,
    handleRequestQuote,
  };
}

